import React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

export type PatientRouteState = {
  patientId: string | null;
  rxId: string | null;
  selectedTab: number;
  doctorReportId: string | null;
  newdoctorReportId: string | null;
  setPatientId: (id: string | null, options?: { replace?: boolean }) => void;
  setRxId: (id: string | null, options?: { replace?: boolean }) => void;
  setSelectedTab: (tab: number, options?: { replace?: boolean }) => void;
  setDoctorReportId: (id: string | null, options?: { replace?: boolean }) => void;
  setNewdoctorReportId: (id: string | null, options?: { replace?: boolean }) => void;
  navigateToTab: (tab: number, options?: { replace?: boolean }) => void;
};

export const usePatientRoute = (): PatientRouteState => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const patientId = searchParams.get("pId");
  const rxId = searchParams.get("rxId");
  const doctorReportId = searchParams.get("doctorReportId");
  const newdoctorReportId = searchParams.get("newdoctorReportId");
  
  // Determine selected tab from URL
  const selectedTab = React.useMemo(() => {
    if (searchParams.has("rxinfo")) return 1;
    if (searchParams.has("termin")) return 2;
    if (searchParams.has("bill")) return 3;
    if (searchParams.has("documents")) return 4;
    if (searchParams.has("medicalDocumentation")) return 5;
    if (searchParams.has("drReports")) return 6;
    if (searchParams.has("sales")) return 7;
    return 0; // Default to patient overview
  }, [searchParams]);

  const updateUrl = React.useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      const newParams = new URLSearchParams(searchParams);
      
      // Clear tab-specific params first
      newParams.delete("rxinfo");
      newParams.delete("termin");
      newParams.delete("bill");
      newParams.delete("documents");
      newParams.delete("medicalDocumentation");
      newParams.delete("drReports");
      newParams.delete("sales");
      
      // Apply updates
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== null) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      
      navigate({ search: `?${newParams.toString()}` }, { replace: options?.replace });
    },
    [navigate, searchParams]
  );

  const setPatientId = React.useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      updateUrl({ pId: id }, options);
    },
    [updateUrl]
  );

  const setRxId = React.useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      updateUrl({ rxId: id }, options);
    },
    [updateUrl]
  );

  const setDoctorReportId = React.useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      updateUrl({ doctorReportId: id }, options);
    },
    [updateUrl]
  );

  const setNewdoctorReportId = React.useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      updateUrl({ newdoctorReportId: id }, options);
    },
    [updateUrl]
  );

  const navigateToTab = React.useCallback(
    (tab: number, options?: { replace?: boolean }) => {
      const updates: Record<string, string | null> = {};
      
      switch (tab) {
        case 1:
          updates.rxinfo = "";
          break;
        case 2:
          updates.termin = "";
          break;
        case 3:
          updates.bill = "";
          break;
        case 4:
          updates.documents = "";
          break;
        case 5:
          updates.medicalDocumentation = "";
          break;
        case 6:
          updates.drReports = "";
          break;
        case 7:
          updates.sales = "";
          break;
        default:
          // No tab param for default case
          break;
      }
      
      updateUrl(updates, options);
    },
    [updateUrl]
  );

  const setSelectedTab = React.useCallback(
    (tab: number, options?: { replace?: boolean }) => {
      navigateToTab(tab, options);
    },
    [navigateToTab]
  );

  return {
    patientId,
    rxId,
    selectedTab,
    doctorReportId,
    newdoctorReportId,
    setPatientId,
    setRxId,
    setSelectedTab,
    setDoctorReportId,
    setNewdoctorReportId,
    navigateToTab,
  };
};
